import { Grid } from '@mui/material'
import FormControlRadio, { radioButtonChoiceOptions } from 'components/FormControlRadio'
import FormInput from 'components/FormInput'
import InputDatePicker from 'components/FormInputDatePicker'
import InputMask from 'components/FormInputMask'
import { FormInputSelect } from 'components/FormInputSelect'
import React, { useEffect, useMemo, useState } from 'react'
import { FieldErrors, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { aaapaiMask } from 'utils/mask.util'
import {
  hearingGeneralDataInit,
  hearingGeneralDataSchema,
  HearingGeneralDataType,
} from 'types/hearing-general-data.type'
import { useLocation, useOutletContext, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useHearingGeneralData } from 'pages/CreateHearing/components/StepGeneralData/hooks/hearing-general-data'
import { zodResolver } from '@hookform/resolvers/zod'
import { OutletContextType } from 'pages/CreateHearing'
import { StepIndex } from 'pages/CreateHearing/enums/step.enum'

export const StepGeneralDataPage: React.FC = () => {
  const { id } = useParams()
  const location = useLocation()
  const { state } = location

  const { formRefs } = useOutletContext<OutletContextType>()

  const {
    hearingGeneralData,
    craaiOptions,
    districtAttorneyOptions,

    fillCraaiOptions,
    fillDistrictAttorneysOptions,

    handleDistrictAttorneyOptions,

    saveGeneralData,
  } = useHearingGeneralData(id)

  const form = useForm<HearingGeneralDataType>({
    resolver: zodResolver(hearingGeneralDataSchema),
    defaultValues: hearingGeneralDataInit,
  })

  const {
    control,
    watch,
    setValue,
    reset,
    formState: { defaultValues, isDirty, dirtyFields },
    clearErrors,
    setError,
    handleSubmit,
  } = form

  const hasDirtyFields = Object.keys(dirtyFields).length > 0 && dirtyFields.constructor === Object

  const [disableNumeroCnj, setDisableNumeroCnj] = useState<boolean>(false)
  const [disableApreendido, setDisableApreendido] = useState<boolean>(false)
  const [disabledCraaiPromotoria, setDisabledCraaiPromotoria] = useState<boolean>(false)

  const [locationState, setLocationState] = useState<{
    numeroCnj: string
    numeroAAAPAI: string
    numeroAIAIBOC: string
    promotoria: string
  }>()

  const craaiValue = watch('craai')
  const isArrestedTypeValue = watch('adolescenteApreendido')
  const aaapaiNumberValue = watch('numeroAaaPai')
  const dataOitivaWatch = watch('dataOitiva')

  const onError = (errors: FieldErrors<HearingGeneralDataType>) => {
    clearErrors()
    const recursiveSetError = (errors: FieldErrors<HearingGeneralDataType>, parentPath = '') => {
      Object.entries(errors).forEach(([key, error]: any) => {
        const path = parentPath ? `${parentPath}.${key}` : key

        if (error?.ref) {
          // Se houver uma referência (ref), usamos o nome da referência para definir o erro
          setError(error.ref.name, { type: error.type, message: error.message })
        } else if (typeof error === 'object') {
          // Recursivamente percorre os objetos internos
          recursiveSetError(error, path)
        }
      })
    }
    recursiveSetError(errors)
    console.log('onError', errors)
    return Promise.reject(errors)
  }

  useEffect(() => {
    ;(async () => {
      if (craaiValue && craaiValue !== '0') {
        const response = await fillDistrictAttorneysOptions(craaiValue)
        if (response) handleDistrictAttorneyOptions(response)
      }
    })()
  }, [craaiValue])

  useEffect(() => {
    if (hearingGeneralData) {
      reset(hearingGeneralData)
    }
  }, [hearingGeneralData])

  useEffect(() => {
    if (craaiValue === '0') {
      setValue('idPromotoria', '0')
    }
  }, [craaiValue, setValue])

  useEffect(() => {
    setValue('numeroAaaPai', aaapaiMask(aaapaiNumberValue))
  }, [setValue, aaapaiNumberValue])

  useEffect(() => {
    if (dataOitivaWatch === '') {
      setValue('dataOitiva', dayjs().format('YYYY-MM-DD'))
    }
  }, [dataOitivaWatch])

  useEffect(() => {
    if (state) {
      const locationState = state as {
        numeroCnj: string
        numeroAAAPAI: string
        numeroAIAIBOC: string
        promotoria: string
      }

      setLocationState(locationState)
    }
  }, [state])

  useEffect(() => {
    ;(async () => {
      if (locationState && defaultValues) {
        const { numeroCnj, numeroAIAIBOC, numeroAAAPAI, promotoria } = locationState

        if (numeroCnj) {
          setValue('numeroCnj', numeroCnj)
          setDisableNumeroCnj(true)
        }

        if (numeroAIAIBOC) {
          setValue('numeroAiai', numeroAIAIBOC)
          setValue('adolescenteApreendido', 'N')
          setDisableApreendido(true)
        }

        if (numeroAAAPAI) {
          setValue('numeroAaaPai', numeroAAAPAI)
          setValue('adolescenteApreendido', 'S')
          setDisableApreendido(true)
        }

        if (promotoria) {
          setDisabledCraaiPromotoria(true)
          const responseCraai = await fillCraaiOptions(promotoria)
          if (responseCraai !== undefined) {
            setValue('craai', responseCraai[0].value)
            const responsePromotoria = await fillDistrictAttorneysOptions(responseCraai[0].value)
            if (responsePromotoria !== undefined) {
              setValue('idPromotoria', responsePromotoria[0].value)
            }
          }
        }
      }
    })()
  }, [locationState, setValue, defaultValues])

  formRefs.current[StepIndex.DADOS_GERAIS] = {
    submit: async () => {
      if (isDirty && hasDirtyFields) {
        await handleSubmit(saveGeneralData, onError)()
      }
      return Promise.resolve()
    },
  }

  const isPromotoriaDisabled = useMemo(() => {
    return disabledCraaiPromotoria || districtAttorneyOptions.length <= 0
  }, [disabledCraaiPromotoria, districtAttorneyOptions])

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(saveGeneralData)}>
        <Grid container spacing={2} rowSpacing={4}>
          <Grid item md={4} xs={12}>
            <FormInputSelect
              control={control}
              name='craai'
              label='CRAAI*'
              options={craaiOptions}
              disabled={disabledCraaiPromotoria}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <FormInputSelect
              control={control}
              name='idPromotoria'
              label='Promotoria de Justiça*'
              disabled={isPromotoriaDisabled}
              options={districtAttorneyOptions}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <InputMask
              control={control}
              name={'numeroCnj'}
              label={'Número CNJ*'}
              mask='0000000.00.0000.0.00.0000'
              placeholder='Informe o número CNJ'
              disabled={disableNumeroCnj}
              autoComplete='off'
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <FormControlRadio
              control={control}
              name='adolescenteApreendido'
              label='O adolescente está apreendido?*'
              disabled={disableApreendido}
              options={radioButtonChoiceOptions}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            {isArrestedTypeValue === 'S' && (
              <InputMask
                control={control}
                name='numeroAaaPai'
                label='N° AAAPAI*'
                placeholder='999-99999/9999'
                mask='000-00000/0000'
                disabled={disableApreendido}
              />
            )}
            {isArrestedTypeValue === 'N' && (
              <InputMask
                control={control}
                name='numeroAiai'
                label='Nº AIAI/BOC*'
                mask='000-00000/0000'
                placeholder='Informe o número do AIAI'
                disabled={disableApreendido}
              />
            )}
          </Grid>
          <Grid item xs>
            <InputDatePicker
              control={control}
              name='dataOitiva'
              label='Data da oitiva*'
              enableFuture
              disablePast={true}
            />
          </Grid>
          <Grid item md={8} xs={12}>
            <FormInput
              control={control}
              name='nomeAdvogado'
              label='Nome do advogado'
              maxLength={60}
              placeholder='Informe o nome do advogado'
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <FormInput
              control={control}
              name='numeroOabDefensor'
              label='Número da OAB ou matrícula do defensor'
              maxLength={20}
              placeholder='Informe o Número da OAB ou matrícula do defensor'
            />
          </Grid>
          <Grid item xs={12}>
            <FormInput
              control={control}
              name='nomeResponsavelAdolescente'
              label='Nome do responsável pelo adolescente'
              maxLength={60}
              placeholder='Informe o nome do responsável pelo adolescente'
            />
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  )
}
